import React, { useState, useMemo } from "react";
import {
  useCreateStagingPromptMutation,
  useCreateProdPromptMutation,
} from "../../../../app/api/console/scanTemplateSyncApiSlice";
import { useGetWebsitesListQuery } from "../../../../app/api/platform/platformWebsitesApiSlice";

const AddWebsitePromptSection = ({ templateId, environment, hasBasePrompt, websitePromptIds }) => {
  const [mode, setMode] = useState(null);
  const [newPromptText, setNewPromptText] = useState("");
  const [selectedWebsiteId, setSelectedWebsiteId] = useState("");
  const [websiteSearch, setWebsiteSearch] = useState("");

  const { data: websites = [], isLoading: isLoadingWebsites } = useGetWebsitesListQuery({ useProd: environment === "prod" });

  const [createStagingPrompt, { isLoading: isCreatingStagingPrompt }] = useCreateStagingPromptMutation();
  const [createProdPrompt, { isLoading: isCreatingProdPrompt }] = useCreateProdPromptMutation();
  const isCreating = environment === "staging" ? isCreatingStagingPrompt : isCreatingProdPrompt;

  // Not memoised on purpose: `websites` comes straight from the RTK Query cache (stable
  // reference) and the `[]` fallback only applies while the list is loading; wrapping it
  // in useMemo would change its identity semantics for the `filteredWebsites` memo below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const websitesList = Array.isArray(websites) ? websites : [];

  const filteredWebsites = useMemo(() => {
    return websitesList
      .filter((w) => !websitePromptIds.includes(w.website_id))
      .filter((w) => !websiteSearch || w.website_name?.toLowerCase().includes(websiteSearch.toLowerCase()) || w.website_url?.toLowerCase().includes(websiteSearch.toLowerCase()));
  }, [websitesList, websitePromptIds, websiteSearch]);

  const selectedWebsite = websitesList.find(w => w.website_id === selectedWebsiteId);

  const handleCreate = async () => {
    if (!newPromptText.trim()) return;
    if (mode === "website" && !selectedWebsiteId) return;
    const createFn = environment === "staging" ? createStagingPrompt : createProdPrompt;
    await createFn({
      templateId,
      prompt: { website_id: mode === "base" ? null : selectedWebsiteId, prompt_text: newPromptText },
    });
    setNewPromptText("");
    setSelectedWebsiteId("");
    setWebsiteSearch("");
    setMode(null);
  };

  const handleCancel = () => {
    setNewPromptText("");
    setSelectedWebsiteId("");
    setWebsiteSearch("");
    setMode(null);
  };

  if (mode) {
    return (
      <div className="rounded-xl border-2 border-dashed border-indigo-300 bg-indigo-50/30 p-5">
        <h4 className="font-semibold text-gray-900 mb-4">
          {mode === "base" ? "Add Base Prompt" : "Add Website-Specific Prompt"}
        </h4>

        {mode === "website" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            {selectedWebsiteId ? (
              <div className="flex items-center gap-2 p-2 bg-white rounded-lg border">
                <div className="w-6 h-6 rounded bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700">
                  {(selectedWebsite?.website_name || "?")[0].toUpperCase()}
                </div>
                <span className="text-sm font-medium">{selectedWebsite?.website_name}</span>
                <span className="text-xs text-gray-400">{selectedWebsite?.website_url}</span>
                <button onClick={() => { setSelectedWebsiteId(""); setWebsiteSearch(""); }} className="ml-auto text-gray-400 hover:text-gray-600">x</button>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  value={websiteSearch}
                  onChange={(e) => setWebsiteSearch(e.target.value)}
                  placeholder="Search by name or URL..."
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 focus:outline-none"
                  autoFocus
                />
                <div className="mt-1 max-h-40 overflow-y-auto border rounded-lg bg-white divide-y">
                  {isLoadingWebsites ? (
                    <div className="px-3 py-4 text-center text-sm text-gray-400">Loading websites...</div>
                  ) : filteredWebsites.length === 0 ? (
                    <div className="px-3 py-4 text-center text-sm text-gray-400">No matching websites</div>
                  ) : (
                    filteredWebsites.slice(0, 20).map((w) => (
                      <button
                        key={w.website_id}
                        onClick={() => { setSelectedWebsiteId(w.website_id); setWebsiteSearch(""); }}
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 transition-colors flex items-center gap-2"
                      >
                        <div className="w-5 h-5 rounded bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700 flex-shrink-0">
                          {(w.website_name || "?")[0].toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{w.website_name}</div>
                          <div className="text-xs text-gray-400 truncate">{w.website_url}</div>
                        </div>
                      </button>
                    ))
                  )}
                  {filteredWebsites.length > 20 && (
                    <div className="px-3 py-2 text-xs text-gray-400 text-center">
                      {filteredWebsites.length - 20} more — refine your search
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Prompt Text</label>
          <textarea
            value={newPromptText}
            onChange={(e) => setNewPromptText(e.target.value)}
            rows={8}
            className="w-full p-3 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 focus:outline-none"
            placeholder={mode === "base" ? "Enter the base prompt instructions..." : `Enter specific instructions for ${selectedWebsite?.website_name || "this website"}...`}
          />
          <span className="text-xs text-gray-400 mt-0.5">{newPromptText.length} chars</span>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleCreate}
            disabled={isCreating || !newPromptText.trim() || (mode === "website" && !selectedWebsiteId)}
            className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {isCreating ? "Creating..." : "Create Prompt"}
          </button>
          <button onClick={handleCancel} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 pt-2">
      {!hasBasePrompt && (
        <button
          onClick={() => setMode("base")}
          className="flex-1 py-3 border-2 border-dashed border-indigo-300 text-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-50 hover:border-indigo-400 transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Add Base Prompt
        </button>
      )}
      <button
        onClick={() => setMode("website")}
        disabled={isLoadingWebsites}
        className="flex-1 py-3 border-2 border-dashed border-amber-300 text-amber-700 rounded-xl text-sm font-medium hover:bg-amber-50 hover:border-amber-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        Add Website Prompt
        {isLoadingWebsites && <span className="text-xs">(loading...)</span>}
      </button>
    </div>
  );
};

export default AddWebsitePromptSection;
